/**
 * Las reglas del volley: medidas de la cancha, física y marcador.
 *
 * Todo lo de este archivo lo necesitan los dos lados —el servidor para simular, el
 * cliente para dibujar y para anticipar— así que vive en shared por el mismo motivo
 * que el catálogo de salas: es lo que convierte "cliente y servidor desalineados" en
 * un error de compilación.
 *
 * **Son funciones puras y constantes, sin estado.** `check:volley` las ejercita sin
 * levantar un servidor ni abrir un socket, y eso es lo que hace que las reglas del
 * marcador —que es donde de verdad duele un bug— se puedan probar en milisegundos.
 */
/**
 * El partido tiene su PROPIO lienzo, y no el del campus.
 *
 * El mundo son 960x540 vistos desde arriba; la cancha se ve de perfil. Son dos
 * cámaras distintas, y meterlas en el mismo sistema de coordenadas obligaría a que la
 * física del balón conviviera con la geometría de las puertas. El panel escala este
 * lienzo a lo que tenga.
 */
export const COURT = { width: 640, height: 360 };
/** El suelo. `y` crece hacia abajo, igual que en el campus y que en Pixi. */
export const GROUND_Y = 320;
export const NET_X = COURT.width / 2;
export const NET_HALF_WIDTH = 3;
/**
 * Alto de la red, y es el número que decide si el juego se puede jugar.
 *
 * Tiene que quedar por encima de las manos de quien está de pie (39) y por debajo de
 * las de quien salta (139), o el golpe de suelo y el remate dejan de ser dos cosas
 * distintas. Y hay que dejar margen suficiente por arriba: un remate sale hacia ABAJO,
 * así que pierde altura mientras cruza los 24 píxeles de la banda de la red — con la
 * red a 110 el balón la rozaba y casi todo remate desde cerca moría ahí.
 */
export const NET_HEIGHT = 95;
export const NET_TOP_Y = GROUND_Y - NET_HEIGHT;
export const PLAYER_WIDTH = 34;
/** El mismo alto que el avatar del campus, para que el arte sea del mismo tamaño. */
export const PLAYER_HEIGHT = 52;
/** Unidades por segundo. Algo más rápido que caminar por el campus: es un deporte. */
export const PLAYER_SPEED = 190;
/**
 * El salto, elegido para que las manos pasen la red y el cuerpo no.
 *
 * La cuenta, que `check:volley` vuelve a hacer para que no se pudra:
 *
 *   cenit  = JUMP_SPEED² / (2 · PLAYER_GRAVITY) = 100 unidades
 *   manos  = cenit + PLAYER_HEIGHT · HIT_CENTER_RATIO = 100 + 39 = 139 sobre el suelo
 *   red    = 110
 *
 * Veintinueve unidades de margen, que es lo que hace que el remate tenga una ventana y
 * no sólo un instante. Desde el piso las manos están a 39 y no llegan ni de lejos: por
 * eso el golpe de suelo cruza en parábola en vez de ir recto.
 *
 * Si tocas cualquiera de estos números, rehaz la cuenta. Un salto que no pasa la red
 * deja el remate inservible, y uno que la pasa de sobra convierte el bloqueo en decorado.
 */
export const JUMP_SPEED = 500;
export const PLAYER_GRAVITY = 1250;
export const BALL_RADIUS = 9;
export const BALL_GRAVITY = 780;
/**
 * Tope de velocidad del balón, y no es capricho de balance: es lo que impide que
 * atraviese la red o el suelo entre dos ticks.
 *
 * A 60 Hz, 700 unidades por segundo son 11,7 por paso, menos que el diámetro del balón
 * (18). Subir esto sin subir el tick devuelve el balón atravesando cosas, que es un bug
 * carísimo de diagnosticar porque sólo pasa a veces.
 */
export const MAX_BALL_SPEED = 700;
/**
 * Hasta dónde alcanza un jugador para tocar el balón, desde el centro de su torso.
 *
 * Generoso a propósito. La ventana real de acierto ya la estrecha la latencia: en la
 * wifi del campus, cien milisegundos son doce unidades de vuelo del balón, y un alcance
 * ajustado convertiría el retardo de red en la mecánica principal del juego.
 */
export const REACH = 34;
/** Cuánto dura la postura de bloqueo y la de recepción, en milisegundos. */
export const BLOCK_MS = 420;
export const RECEIVE_MS = 380;
/** Enfriamiento entre acciones, para que machacar el botón no sea estrategia. */
export const ACTION_COOLDOWN_MS = 180;
/**
 * Golpe desde el PISO: parábola hacia arriba.
 *
 * Es una colocada, no un ataque: el balón sube y cruza despacio, dando tiempo al rival
 * a colocarse. Que este golpe exista —y que sea flojo— es lo que hace que saltar valga
 * la pena, porque la alternativa siempre está disponible y siempre es peor.
 *
 * Su alcance son unas 300 unidades, algo menos que media cancha. No llega desde el
 * fondo, y eso es deseable: obliga a trabajar el balón hacia adelante en vez de
 * devolverlo todo de un manotazo. Pegado a la red tampoco sirve —la parábola se estrella
 * contra ella— que es exactamente cuando toca saltar.
 */
export const GROUND_HIT_VX = 240;
export const GROUND_HIT_VY = -450;
/**
 * El saque: más largo y mucho más alto que una colocada.
 *
 * Tiene números propios porque tiene un trabajo propio: salir desde el fondo del campo y
 * pasar la red **con margen**. Reutilizar el golpe de suelo fue el primer intento y falló
 * en vivo — el sacador estaba pegado a la red, el balón salía por debajo de su altura y
 * el saque se estrellaba contra ella regalando el punto. De ahí salieron estas dos
 * constantes y `servePosition`.
 */
export const SERVE_VX = 250;
export const SERVE_VY = -480;
/**
 * Remate EN EL AIRE: directo y hacia abajo.
 *
 * Mismo botón que el golpe de piso; lo que cambia es que el servidor ve al jugador en
 * el aire. Rápido y descendente, así que llega antes y bota cerca: la respuesta es
 * recibir, no correr.
 */
export const SPIKE_VX = 520;
export const SPIKE_VY = 330;
/**
 * La plancha: tirarse a salvar un balón que de pie no se alcanza.
 *
 * Es la acción que le da sentido a `MAX_TOUCHES`. Sin ella, casi todo se resuelve de un
 * toque y los tres que permite el reglamento son decoración; con ella aparece la secuencia
 * de verdad —**salvar, colocar, rematar**—, que es a lo que se parece el volley.
 *
 * Su precio es quedarse en el suelo un momento. Sin ese coste sería estrictamente mejor
 * que estar de pie, y entonces se jugaría el partido entero tirándose.
 */
export const DIVE_MS = 420;
export const DIVE_RECOVER_MS = 380;
/** Cuánto se desliza, en unidades por segundo. Bastante más rápido que caminar. */
export const DIVE_SPEED = 330;
/**
 * Estirado se llega más lejos y más bajo, que es justo lo que no se alcanza de pie.
 *
 * El alcance crece y el punto de golpeo baja casi al suelo: la plancha no sirve para
 * balones altos —para eso está el salto— sino para los que van a morir en la arena.
 */
export const DIVE_REACH = REACH + 30;
export const DIVE_HIT_RATIO = 0.28;
/**
 * El balón salvado sube MUCHO y se mueve poco.
 *
 * Es lo que convierte la plancha en una jugada y no en un manotazo: sale casi vertical y
 * bien alto, así que da tiempo de levantarse y prepararle un remate. Mandarlo al otro
 * campo desde el suelo sería más fácil y mucho menos interesante.
 */
export const DIVE_VY = -520;
export const DIVE_VX = 70;
/** Bloqueo: devuelve el balón por donde vino, frenado y hacia arriba. */
export const BLOCK_VX_KEEP = 0.45;
export const BLOCK_VY = -190;
/** Recepción: absorbe la velocidad que traiga y lo levanta. El antídoto del remate. */
export const RECEIVE_VX = 120;
export const RECEIVE_VY = -400;
/** La red no da punto: rebota. Una regla menos que explicar y un rally más largo. */
export const NET_BOUNCE = 0.4;
export const POINTS_TO_WIN = 11;
/** A 11-11 se sigue hasta sacar dos de ventaja, como en el volley de verdad. */
export const MIN_LEAD = 2;
/**
 * Toques seguidos por lado antes de perder el punto.
 *
 * Tres, como el volley real. La diferencia es que aquí **el mismo jugador puede dar los
 * tres**: con uno por lado, prohibir el toque doble dejaría la recepción sin salida
 * —levantas el balón y no puedes rematarlo— y el rally moriría en el primer toque de
 * cada lado.
 */
export const MAX_TOUCHES = 3;
/**
 * Techo de duración del partido.
 *
 * La ventaja de dos puede no llegar nunca, y una sala viva para siempre es una fuga de
 * memoria con forma de reglamento. Al vencer gana quien vaya arriba; empatados no gana
 * nadie y no se paga.
 */
export const MATCH_MAX_MS = 12 * 60_000;
/** Si el sacador no saca, saca el reloj: un jugador ausente no congela el partido. */
export const SERVE_AUTO_MS = 4000;
/** Pausa entre el punto y el saque siguiente, para que se vea qué pasó. */
export const POINT_PAUSE_MS = 1500;
/**
 * Lo que cobra el ganador.
 *
 * Treinta contra una tienda que entera cuesta 132: la gorra ESPOL sale a las dos
 * victorias y el catálogo completo a las cinco. Deja sitio a ítems caros más adelante,
 * que es lo que un premio de cien —una tienda y media por partido— no dejaba.
 */
export const VOLLEY_WIN_REWARD = 30;
/**
 * Techo por ventana, y aquí sí hay un ataque concreto detrás.
 *
 * Con dos jugadores aparece algo que un juego de un jugador no puede sufrir: dos cuentas
 * turnándose las victorias. No hacen trampa —juegan de verdad y pierden a propósito— así
 * que no hay nada que detectar; lo único que funciona es acotar el botín. Seis victorias
 * pagadas por hora están muy por encima de lo que da el juego honesto (un partido a 11
 * dura minutos) y muy por debajo de lo que necesita el que farmea.
 *
 * La ventana se cuenta sobre `volley_win` y no compartida con `game_reward`: el libro
 * consulta por una razón, y hacerlas compartir techo obligaría a tocar `ledger.ts` para
 * acoplar dos módulos que deben ignorarse.
 */
export const VOLLEY_COIN_CAP = 200;
export const VOLLEY_COIN_CAP_WINDOW_MS = 60 * 60 * 1000;
/**
 * Puntos jugados mínimos para que un partido pague.
 *
 * Existe por los abandonos. Sin esto, entrar y salir de inmediato le regalaría la
 * victoria al cómplice una y otra vez sin jugar; con esto, abandonar sólo paga cuando ya
 * hubo partido de verdad — y a la vez el que se enfada y cierra la pestaña no puede
 * dejar sin premio a quien le estaba ganando. Un partido normal a 11 lo supera siempre.
 */
export const MIN_POINTS_FOR_REWARD = 6;
export const VOLLEY_ROOM_NAME = "volley";
export const VOLLEY_MAX_CLIENTS = 2;
/**
 * El partido simula a 60 Hz y sincroniza a 30, contra los 20/15 del campus.
 *
 * Un balón necesita más resolución que un avatar caminando, y son dos clientes por sala
 * en vez de cincuenta. Van acá y **no** en `constants.ts`: cambiar `TICK_RATE` movería
 * el campus entero.
 */
export const VOLLEY_TICK_RATE = 60;
export const VOLLEY_PATCH_RATE = 30;
/** Tope de acciones por ventana. Regla 6: el límite es del servidor, no del botón. */
export const VOLLEY_ACTION_BURST = 12;
export const VOLLEY_ACTION_WINDOW_MS = 1000;
/**
 * En qué va el partido. El índice es lo que viaja por el cable, así que **es
 * append-only**, igual que `AVATAR_COLORS` y `FACINGS`: reordenar esta lista le cambia
 * la fase a todo el mundo en silencio.
 */
export const VOLLEY_PHASES = ["esperando", "saque", "rally", "punto", "fin"];
/** Qué está haciendo un jugador. Índice por el cable: append-only, como las fases. */
export const VOLLEY_STANCES = [
    "reposo",
    "corriendo",
    "aire",
    "bloqueo",
    "recepcion",
    "golpe",
    "remate",
    /* Las dos de la plancha van AL FINAL, que es lo que manda un índice que viaja por el
       cable: insertarlas junto a las demás posturas le habría cambiado la suya a todo el
       mundo en silencio. */
    "plancha",
    "levantandose",
];
export function phaseIndex(phase) {
    return VOLLEY_PHASES.indexOf(phase);
}
export function stanceIndex(stance) {
    return VOLLEY_STANCES.indexOf(stance);
}
/**
 * Hasta dónde llega un jugador por su lado.
 *
 * Nadie cruza la red: el borde interior es el poste, no el centro. Lo calcula shared
 * para que el servidor recorte y el cliente pinte exactamente el mismo límite.
 */
export function sideBounds(side) {
    const half = PLAYER_WIDTH / 2;
    const inner = NET_X - NET_HALF_WIDTH - half;
    return side === 0
        ? { min: half, max: inner }
        : { min: COURT.width - inner, max: COURT.width - half };
}
/**
 * Desde dónde se saca: a un tercio del fondo del propio campo.
 *
 * Al sacador se le COLOCA aquí, no se le pide que venga. Sin esto, quien acababa de
 * ganar el punto pegado a la red sacaba desde ahí, y un saque que sale por debajo del
 * borde de la red sólo puede estrellarse contra ella — punto para el rival, sin que el
 * jugador hiciera nada mal. En el volley real también se saca desde el fondo.
 */
export function servePosition(side) {
    const bounds = sideBounds(side);
    const span = bounds.max - bounds.min;
    return side === 0 ? bounds.min + span / 3 : bounds.max - span / 3;
}
/** Hacia dónde ataca este lado: el izquierdo manda a la derecha, y al revés. */
export function attackDirection(side) {
    return side === 0 ? 1 : -1;
}
/** De qué lado de la red cae este punto. */
export function sideOf(x) {
    return x < NET_X ? 0 : 1;
}
/**
 * Quién ganó el partido, o `null` si sigue.
 *
 * Es la regla del enunciado y la única que no admite aproximaciones: hay que llegar a
 * 11 **y** sacar dos de ventaja, así que 11-10 no cierra nada y 13-11 sí. Se escribe
 * una vez, acá, y la ejercita `check:volley` con la tabla de casos límite.
 */
export function matchWinner(scoreA, scoreB) {
    if (scoreA >= POINTS_TO_WIN && scoreA - scoreB >= MIN_LEAD)
        return 0;
    if (scoreB >= POINTS_TO_WIN && scoreB - scoreA >= MIN_LEAD)
        return 1;
    return null;
}
/**
 * Quién gana si se acaba el tiempo: el que vaya arriba, o nadie si están empatados.
 *
 * Aparte de `matchWinner` a propósito. Ganar por reglamento y ganar por el reloj son
 * dos cosas distintas, y mezclarlas en una sola función haría que un 5-4 en el minuto
 * tres cerrara el partido.
 */
export function winnerOnTime(scoreA, scoreB) {
    if (scoreA === scoreB)
        return null;
    return scoreA > scoreB ? 0 : 1;
}
/**
 * A qué altura del cuerpo se golpea, como fracción del alto.
 *
 * A tres cuartos: ni los pies ni exactamente la coronilla. Con `REACH` alcanza desde
 * algo por encima de la cintura hasta bastante sobre la cabeza, que es lo que hace
 * creíble tanto una recepción baja como un remate en el aire sin necesitar un punto de
 * golpeo distinto por acción.
 */
export const HIT_CENTER_RATIO = 0.75;
/** Dónde tiene las manos un jugador cuyos pies están en (x, y). */
export function hitCenter(x, y) {
    return { x, y: y - PLAYER_HEIGHT * HIT_CENTER_RATIO };
}
/**
 * ¿Puede este jugador tocar el balón?
 *
 * Vive en shared para que el servidor la use como verdad y el cliente pueda pintar el
 * aviso de "ya" con exactamente el mismo criterio. Que el jugador vea un indicador que
 * no coincide con lo que el servidor acepta es peor que no tener indicador.
 */
export function ballInReach(ball, feetX, feetY) {
    const hands = hitCenter(feetX, feetY);
    return Math.hypot(ball.x - hands.x, ball.y - hands.y) <= REACH + BALL_RADIUS;
}
/** Dónde tiene las manos quien está estirado en el suelo: más bajo y más lejos. */
export function diveHitCenter(x, y) {
    return { x, y: y - PLAYER_HEIGHT * DIVE_HIT_RATIO };
}
/**
 * ¿Puede alcanzarlo tirándose?
 *
 * Va aparte de `ballInReach` y no como un parámetro suyo porque son dos preguntas
 * distintas: una decide si golpeas de pie, la otra si te salvas. Mezclarlas en una función
 * con una bandera es como se acaba llamándola con el valor equivocado desde el bot.
 */
export function ballInDiveReach(ball, feetX, feetY) {
    const hands = diveHitCenter(feetX, feetY);
    return Math.hypot(ball.x - hands.x, ball.y - hands.y) <= DIVE_REACH + BALL_RADIUS;
}
/** Recorta un vector a `MAX_BALL_SPEED` conservando su dirección. */
export function clampBallSpeed(vx, vy) {
    const speed = Math.hypot(vx, vy);
    if (speed <= MAX_BALL_SPEED || speed === 0)
        return { vx, vy };
    const factor = MAX_BALL_SPEED / speed;
    return { vx: vx * factor, vy: vy * factor };
}
//# sourceMappingURL=rules.js.map